import isEqual from 'lodash.isequal';
import { useCallback, useMemo, useState } from 'react';
import { useAllLinkIdsMap } from './use-all-link-ids-map';
import { isObject } from '@monash/portal-frontend-common';

// Exposes list of checked links and useful checkbox related states/operations
export const useCheckedLinks = ({ links }) => {
  const [checkedLinkIds, setCheckedLinkIds] = useState({}); // map of links id's that are currently checked

  const checkedLinks = useMemo(() => {
    return links.filter((link) => {
      const id = link.id;
      return checkedLinkIds[id];
    });
  }, [links, checkedLinkIds]);

  const allIdsChecked = useAllLinkIdsMap({
    links,
    value: true,
  });

  const isAllChecked = isEqual(checkedLinkIds, allIdsChecked);

  const handleCheck = useCallback(
    (id, e) => {
      const checked = e?.target.checked;
      setCheckedLinkIds((prevCheckedLinkIds) => {
        const newCheckedLinkIds = { ...prevCheckedLinkIds };
        newCheckedLinkIds[id] = checked;
        return newCheckedLinkIds;
      });
    },
    [setCheckedLinkIds]
  );

  const handleCheckAll = () => {
    setCheckedLinkIds((prevCheckedLinkIds) => {
      // if all are currently checked => uncheck all
      if (isEqual(prevCheckedLinkIds, allIdsChecked)) {
        return {};
      } else {
        // if not all are currently checked => check all
        return allIdsChecked;
      }
    });
  };

  const updateChecked = (toUpdate, merge = false) => {
    if (!isObject(toUpdate)) {
      return;
    }

    setCheckedLinkIds((prevCheckedLinkIds) => {
      const parsedUpdate = Object.fromEntries(
        Object.entries(toUpdate).filter(
          ([linkId, isChecked]) =>
            linkId in prevCheckedLinkIds && typeof isChecked === 'boolean'
        )
      );

      if (merge) {
        return {
          ...prevCheckedLinkIds,
          ...parsedUpdate,
        };
      } else {
        return parsedUpdate;
      }
    });
  };

  const isChecked = (id) => {
    return Boolean(checkedLinkIds[id]);
  };

  return {
    checkedLinks,
    handleCheck,
    handleCheckAll,
    updateChecked,
    isChecked,
    isAllChecked,
  };
};
